import styled, { css } from 'styled-components';
import { Avatar } from '@99designs/emu';
import { avatars, colors, typography } from '@99designs/emu/settings';

export enum Arrow {
    TopLeft = 'topLeft',
    TopRight = 'topRight',
    BottomLeft = 'bottomLeft',
    BottomRight = 'bottomRight',
}

export interface Props {
    arrow: Arrow;
    text: string;
    link: string;
    avatar: {
        src: string;
        alt: string;
    };
    backgroundColor?: string;
    responsive?: boolean; // Use this to control the width with an outside, layout component
    reverse?: boolean;
    darkText?: boolean;
}

const AttributionLink = styled.a<{
    backgroundColor: string;
    arrow: Arrow;
    responsive: boolean;
    reverse: boolean;
}>`
    display: flex;
    align-items: center;
    position: relative;
    width: max-content;
    border-radius: 20px;
    padding: 3px 16px 3px 3px;
    gap: 10px;
    font-family: ${typography.fontFamilies.heading};
    font-weight: ${typography.fontWeights.heading};
    font-size: ${typography.fontSizes.paragraphs.small};
    line-height: ${typography.lineHeights.medium};
    text-align: center;

    ${({ backgroundColor }) => css`
        background: ${backgroundColor};
    `}

    ${({ reverse }) =>
        reverse &&
        css`
            padding: 3px 3px 3px 16px;
            flex-direction: row-reverse;
        `}

    ${({ responsive }) =>
        responsive &&
        css`
            max-width: 100%;
        `}

    &:hover {
        text-decoration: none;
    }

    &:before {
        content: '';
        position: absolute;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 0 8px 13.9px 8px;
        border-radius: 2px;
        ${({ backgroundColor }) => css`
            border-color: transparent transparent ${backgroundColor} transparent;
        `}

        ${({ arrow }) => {
            switch (arrow) {
                case Arrow.TopLeft:
                    return css`
                        top: -8px;
                        left: -8px;
                        transform: rotate(72deg);
                    `;
                case Arrow.TopRight:
                    return css`
                        top: -4px;
                        right: -10px;
                        transform: rotate(168deg);
                    `;
                case Arrow.BottomLeft:
                    return css`
                        bottom: -8px;
                        left: -8px;
                        transform: rotate(107deg);
                    `;
                case Arrow.BottomRight:
                    return css`
                        bottom: -4px;
                        right: -10px;
                        transform: rotate(12deg);
                    `;
            }
        }}
`;

const StyledAvatar = styled.span`
    width: ${avatars.sizes[avatars.size.ExtraSmall].avatarSize}px;
    height: ${avatars.sizes[avatars.size.ExtraSmall].avatarSize}px;
    flex-shrink: 0;
`;

const StyledText = styled.span<{ darkText: boolean }>`
    color: ${colors.white};

    ${({ darkText }) =>
        darkText &&
        css`
            color: ${colors.grey800};
        `}
`;

export function FeatureAttribution(props: Props) {
    const {
        avatar,
        text,
        link,
        arrow,
        backgroundColor = colors.heritage,
        responsive = false,
        reverse = false,
        darkText = false,
    } = props;

    return (
        <AttributionLink
            href={link}
            backgroundColor={backgroundColor}
            arrow={arrow}
            responsive={responsive}
            reverse={reverse}
        >
            <StyledAvatar>
                <Avatar alt={avatar.alt} url={avatar.src} size={avatars.size.ExtraSmall} />
            </StyledAvatar>
            <StyledText darkText={darkText}>{text}</StyledText>
        </AttributionLink>
    );
}
